import { Col, Row } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { CopyableLink } from "../../../../components/CopyableLink.tsx"
import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput.tsx"
import { WorkspaceCard } from "../../../Lead/LeadWorkspacePage/cards/WorkspaceCard.tsx"
import {
  type Customer,
  type CustomerMutateData
} from "../../types/customer.types.ts"
import { CustomerDetailField } from "../CustomerDetailField.tsx"
import {
  type CustomerCardField,
  type CustomerCardSpec
} from "./customer-card-fields.ts"

type CustomerCardValues = Partial<Record<CustomerCardField["name"], string>>

interface CustomerFieldsCardProps {
  spec: CustomerCardSpec
  customer: Customer
  canEdit?: boolean
  onSave: (changed: CustomerMutateData) => Promise<boolean>
  viewExtra?: ReactNode
}

const cardValues = (spec: CustomerCardSpec, customer: Customer) =>
  spec.fields.reduce<CustomerCardValues>((values, field) => {
    values[field.name] = customer?.[field.name] ?? ""
    return values
  }, {})

function CustomerFieldsCard({
  spec,
  customer,
  canEdit,
  onSave,
  viewExtra
}: CustomerFieldsCardProps) {
  const { t } = useTranslation()

  const values = cardValues(spec, customer)

  const renderValue = (field: CustomerCardField, value?: string) => {
    if (field.kind === "email")
      return (
        <CopyableLink type={"email"} href={value!}>
          {value}
        </CopyableLink>
      )
    if (field.kind === "phone")
      return (
        <CopyableLink type={"phone"} href={value!}>
          {value}
        </CopyableLink>
      )
    return undefined
  }

  return (
    <WorkspaceCard<CustomerCardValues>
      cardId={spec.cardId}
      title={t(spec.titleKey)}
      canEdit={canEdit}
      values={values}
      onSave={(changed) => onSave(changed as CustomerMutateData)}
      renderEdit={({ values, setValues, saving }) => (
        <Row gutter={[12, 8]}>
          {spec.fields.map((field) => {
            const label = t(field.labelKey)
            const value = values[field.name] ?? ""
            const onChange = (
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => setValues({ [field.name]: event.target.value })

            return (
              <Col key={field.name} xs={24} sm={field.span ?? 12}>
                <CustomerDetailField label={label}>
                  {field.kind === "notes" ? (
                    <TextArea
                      rows={3}
                      disabled={saving}
                      aria-label={label}
                      value={value}
                      onChange={onChange}
                    />
                  ) : field.kind === "phone" ? (
                    <PhoneInput
                      disabled={saving}
                      aria-label={label}
                      value={value}
                      onChange={onChange}
                    />
                  ) : (
                    <TextInput
                      disableAutoCapitalize={
                        field.kind === "email" || field.kind === "zip"
                      }
                      disabled={saving}
                      aria-label={label}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                </CustomerDetailField>
              </Col>
            )
          })}
        </Row>
      )}
    >
      <Row gutter={[12, 8]}>
        {spec.fields.map((field) => {
          const value = values[field.name]

          return (
            <Col key={field.name} xs={24} sm={field.span ?? 12}>
              <CustomerDetailField
                label={t(field.labelKey)}
                value={value}
                placeholder={
                  field.kind === "notes"
                    ? t("customerDetail.empty.notes")
                    : undefined
                }
              >
                {renderValue(field, value)}
              </CustomerDetailField>
            </Col>
          )
        })}
      </Row>
      {viewExtra}
    </WorkspaceCard>
  )
}

export { type CustomerCardValues, CustomerFieldsCard }
